import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography
} from '@mui/material';
import DashboardCard from '../components/DashboardCard';
import {
    GoogleMap,
    Marker
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { api } from 'common';
import { colors } from 'components/Theme/WebTheme';
import { FONT_FAMILY } from 'common/sharedFunctions';

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);
    const [selectedMarkerId, setSelectedMarkerId] = useState(null);
    const [mapReady, setMapReady] = useState(false);
    const [settings, setSettings] = useState({});
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const [mapFilter, setMapFilter] = useState('all');
    const [currentTime, setCurrentTime] = useState(Date.now());

    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const dispatch = useDispatch();
    const { fetchDrivers, clearFetchDrivers } = api;

    const VIENNA_CENTER = { lat: 48.2082, lng: 16.3738 };

    // Filter markers based on selection
    const markersToShow =
        mapFilter === 'activeDrivers'
            ? locations.filter(l => l && l.active === true)
            : locations; // Show ALL drivers (both active and offline)

    // Find selected marker data
    const selectedMarkerData = selectedMarkerId ? locations.find(m => m.id === selectedMarkerId) : null;

    function formatAmount(value, decimal, country) {
        const number = parseFloat(value || 0);
        if (country === "Vietnam") {
            return number.toLocaleString("vi-VN", {
                minimumFractionDigits: decimal,
                maximumFractionDigits: decimal
            });
        } else {
            return number.toLocaleString("en-US", {
                minimumFractionDigits: decimal,
                maximumFractionDigits: decimal
            });
        }
    }

    function formatDurationMs(value) {
        const totalMs = Math.max(0, Number(value) || 0);
        const totalSeconds = Math.floor(totalMs / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (days > 0) {
            return `${days}d ${hours}h ${minutes}m`;
        }
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        }
        return `${seconds}s`;
    }

    function formatTimestamp(value) {
        const timestamp = Number(value || 0);
        if (!timestamp) {
            return '-';
        }

        const date = new Date(timestamp);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }

        return date.toLocaleString(undefined, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    function renderStatusBadge(driver) {
        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                color: driver.active ? colors.GREEN : colors.RED,
                fontWeight: 600,
                whiteSpace: 'nowrap'
            }}>
                <span style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: driver.active ? colors.GREEN : colors.RED,
                    display: 'inline-block'
                }} />
                {driver.active ? t('active') : t('inactive')}
            </span>
        );
    }

    function renderDriverStatTile(label, value) {
        return (
            <Box sx={{
                backgroundColor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '14px',
                padding: '10px 12px',
                minWidth: 0
            }}>
                <Typography sx={{
                    color: '#64748B',
                    fontSize: 12,
                    lineHeight: 1.2,
                    fontFamily: FONT_FAMILY,
                    marginBottom: '5px'
                }}>
                    {label}
                </Typography>
                <Typography sx={{
                    color: '#0F172A',
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    fontFamily: FONT_FAMILY,
                    overflowWrap: 'anywhere'
                }}>
                    {value}
                </Typography>
            </Box>
        );
    }

    useEffect(() => {
    dispatch(fetchDrivers('web'));
    return () => {
        dispatch(clearFetchDrivers());
    };
}, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentTime(Date.now());
        }, 30000);

        return () => clearInterval(intervalId);
    }, []);


   /*  useEffect(() => {
        console.log('[DEBUG] Raw usersdata.drivers:', usersdata.drivers);
        console.log('[DEBUG] usersdata.drivers length:', usersdata.drivers ? usersdata.drivers.length : 'null');
        console.log('[DEBUG] usersdata.users length:', usersdata.users ? usersdata.users.length : 'null');
        console.log('[DEBUG] Auth profile usertype:', auth.profile.usertype);
        console.log('[DEBUG] Auth profile uid:', auth.profile.uid);
    }, [usersdata.drivers, usersdata.users, auth.profile]); */

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    const mapCenter = mylocation?.lat ? mylocation : VIENNA_CENTER;

    const driverStats = usersdata.users
        ? usersdata.users
            .filter(user =>
                user.usertype === 'driver' &&
                ((auth.profile.usertype === 'fleetadmin' && user.fleetadmin === auth.profile.uid) ||
                    auth.profile.usertype === 'admin')
            )
            .map((driver, index) => {
                const currentSessionMs =
                    driver.driverActiveStatus === true && driver.driverOnlineSessionStartedAt
                        ? Math.max(0, currentTime - Number(driver.driverOnlineSessionStartedAt))
                        : 0;
                const completedSessions = Number(driver.driverOnlineSessions || 0);
                const totalOnlineMs = Number(driver.driverOnlineDurationMs || 0) + currentSessionMs;
                const lastOnlineAt = driver.driverActiveStatus === true
                    ? driver.driverOnlineSessionStartedAt || driver.driverLastOnlineStatusChangeAt
                    : driver.driverLastOnlineSessionEndedAt || driver.driverLastOnlineStatusChangeAt;

                return {
                    id: driver.uid || driver.id || `${driver.firstName}-${driver.lastName}-${index}`,
                    name: `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || '-',
                    active: driver.driverActiveStatus === true,
                    totalOnlineMs,
                    totalOnlineLabel: formatDurationMs(totalOnlineMs),
                    sessions: completedSessions,
                    currentSessionLabel: currentSessionMs > 0 ? formatDurationMs(currentSessionMs) : '-',
                    lastSessionLabel: driver.driverLastOnlineDurationMs
                        ? formatDurationMs(driver.driverLastOnlineDurationMs)
                        : '-',
                    lastOnlineLabel: formatTimestamp(lastOnlineAt),
                };
            })
            .sort((a, b) => {
                if (b.totalOnlineMs !== a.totalOnlineMs) {
                    return b.totalOnlineMs - a.totalOnlineMs;
                }
                if (b.sessions !== a.sessions) {
                    return b.sessions - a.sessions;
                }
                return a.name.localeCompare(b.name);
            })
        : [];

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    /* useEffect(() => {
        console.log('[DEBUG] mapReady:', mapReady);
        console.log('[DEBUG] selectedMarkerId:', selectedMarkerId);
        console.log('[DEBUG] selectedMarkerData:', selectedMarkerData);
        console.log('[DEBUG] locations count:', locations.length);
        console.log('[DEBUG] markersToShow count:', markersToShow.length);
        if (selectedMarkerData) {
            console.log('[DEBUG] Selected marker has data:', {
                name: selectedMarkerData.drivername,
                car: selectedMarkerData.carnumber,
                lat: selectedMarkerData.lat,
                lng: selectedMarkerData.lng
            });
        }
    }, [mapReady, selectedMarkerId, selectedMarkerData, locations, markersToShow]); */

    useEffect(() => {
  if (!usersdata.users || usersdata.users.length === 0) {
    setLocations([]);
    setSelectedMarkerId(null);
    return;
  }

  let driverUsers = usersdata.users.filter(u => u.usertype === 'driver');

  if (auth.profile.usertype === 'fleetadmin') {
    driverUsers = driverUsers.filter(
      u => u.fleetadmin === auth.profile.uid
    );
  }

  let locs = [];

  for (let i = 0; i < driverUsers.length; i++) {
    const d = driverUsers[i];

    if (!d.location || !d.location.lat || !d.location.lng) continue;

    let carImage =
      'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';

    for (let j = 0; j < cars.length; j++) {
      if (cars[j].name === d.carType) {
        carImage = cars[j].image;
        break;
      }
    }

    const markerId = d.uid || d.email || `${d.firstName}-${d.lastName}-${i}`;

    locs.push({
      id: markerId,
      uid: d.uid,
      lat: d.location.lat,
      lng: d.location.lng,
      drivername: `${d.firstName} ${d.lastName}`,
      carnumber: d.vehicleNumber,
      cartype: d.carType,
      carImage,
      active: d.driverActiveStatus === true,
      fleetadmin: d.fleetadmin,
    });
  }

  setLocations(locs);
}, [usersdata.users, cars, auth.profile]);


    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin') || auth.profile.usertype === 'admin')) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(fleetCommission);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings, auth.profile.uid, auth.profile.usertype]);

    const barChartOptions = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        colors: [colors.GREEN, colors.RED],
        options: {
            maintainAspectRatio: false,
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                        },
                    },
                ],
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth'
            },
        },
        chart: {
            height: 350,
            type: 'area',
            fontFamily: FONT_FAMILY
        },
        xaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY
                }
            }
        },
        title: {
            style: {
                fontFamily: FONT_FAMILY
            }
        },
        subtitle: {
            style: {
                fontFamily: FONT_FAMILY
            }
        },
        tooltip: {
            style: {
                fontFamily: FONT_FAMILY
            }
        },
        legend: {
            fontFamily: FONT_FAMILY
        }
    };

    const barChart = [{
        name: t('completed_bookings'),
        data: allCompleteCount
    }, {
        name: t('cancelled_bookings'),
        data: allCancelCount
    }];

    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0);
            setDriverCount(0);
            setCustomerCount(0);
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (months[i] === months[tDate.getMonth()] && (bookinglistdata.bookings[j].status === 'PAID' || bookinglistdata.bookings[j].status === 'COMPLETE')) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && bookinglistdata.bookings[j].status === 'CANCELLED') {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <div>
                <Typography variant="h4" style={{ margin: "20px 20px 0 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left', fontFamily: FONT_FAMILY }}>{t('gross_earning')}</Typography>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.WALLET_CARD, color: colors.WHITE }} title={t('today_text')} image={require("../assets/img/money1.jpg").default}>{settings.symbol + ' ' + formatAmount(dailygross, settings.decimal, settings.country)}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.WALLET_CARD, color: colors.WHITE }} title={t('today_text')} image={require("../assets/img/money1.jpg").default}>{formatAmount(dailygross, settings.decimal, settings.country) + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.VIOLET, color: colors.WHITE }} title={t('this_month_text')} image={require("../assets/img/money2.jpg").default}>{settings.symbol + ' ' + formatAmount(monthlygross, settings.decimal, settings.country)}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.VIOLET, color: colors.WHITE }} title={t('this_month_text')} image={require("../assets/img/money2.jpg").default}>{formatAmount(monthlygross, settings.decimal, settings.country) + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.GREEN, color: colors.WHITE }} title={t('total')} image={require("../assets/img/money3.jpg").default}>{settings.symbol + ' ' + formatAmount(totalgross, settings.decimal, settings.country)}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.GREEN, color: colors.WHITE }} title={t('total')} image={require("../assets/img/money3.jpg").default}>{formatAmount(totalgross, settings.decimal, settings.country) + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                </Grid>

                <Grid item xs={12} style={{ marginTop: 20 }}>
                    <Grid container style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
                        <Grid item xs={12} sm={12} md={9} lg={9} style={{ backgroundColor: colors.WHITE, borderRadius: 20, overflow: 'hidden' }} boxShadow={3}>
                            <Typography variant="h4" style={{ margin: "20px 20px 0 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left', fontFamily: FONT_FAMILY }}>{t('real_time_driver_section_text')}</Typography>
                            <div style={{ position: 'relative' }}>
                                <GoogleMap
                                    zoom={10}
                                    center={mapCenter}
                                    mapContainerStyle={{ height: '380px', width: '100%' }}
                                    onLoad={() => {
                                        //console.log('[DEBUG] Map loaded');
                                        setMapReady(true);
                                    }}
                                >
                                    {markersToShow && markersToShow.length > 0 && mapReady && markersToShow.map((marker, index) => {
                                        if (!marker || !marker.id) {
                                            //console.log('[DEBUG] Marker without ID at index:', index, marker);
                                            return null;
                                        }
                                        return (
                                            <Marker
                                                key={`marker-${marker.id}`}
                                                position={{ lat: marker.lat, lng: marker.lng }}
                                                icon={{
                                                    url: marker.carImage || 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png',
                                                    scaledSize: new window.google.maps.Size(35, 25),
                                                }}
                                                onClick={() => {
                                                    //console.log('[DEBUG] Marker clicked with ID:', marker.id);
                                                    if (marker.id) {
                                                        setSelectedMarkerId(marker.id);
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </GoogleMap>

                                {selectedMarkerData !== null && selectedMarkerData !== undefined && (
                                    <div style={{
                                        position: 'fixed',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        backgroundColor: 'white',
                                        padding: '20px',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                        zIndex: 1000,
                                        minWidth: '280px'
                                    }}>
                                        <button
                                            onClick={() => setSelectedMarkerId(null)}
                                            style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '20px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ×
                                        </button>
                                        <div style={{ fontFamily: FONT_FAMILY, color: '#000', paddingTop: '10px' }}>
                                            {selectedMarkerData.drivername && (
                                                <div style={{ marginBottom: '10px' }}>
                                                    <strong style={{ fontSize: '16px' }}>{selectedMarkerData.drivername}</strong>
                                                </div>
                                            )}
                                            {selectedMarkerData.carnumber && (
                                                <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                                                    <strong>Fahrzeug:</strong> {selectedMarkerData.carnumber}
                                                </div>
                                            )}
                                            {selectedMarkerData.cartype && (
                                                <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                                                    <strong>Typ:</strong> {selectedMarkerData.cartype}
                                                </div>
                                            )}
                                            <div style={{ fontSize: '14px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '10px',
                                                    height: '10px',
                                                    borderRadius: '50%',
                                                    backgroundColor: selectedMarkerData.active ? '#4CAF50' : '#999',
                                                    marginRight: '8px'
                                                }}></span>
                                                <strong>{selectedMarkerData.active ? 'Aktiv 🟢' : 'Offline 🔴'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} style={{ padding: 25, marginTop: '30px' }}>
                            <Card style={{ borderRadius: "19px", backgroundColor: colors.WALLET_CARD, minHeight: 100, marginBottom: 20 }}>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 20, marginTop: 20, fontFamily: FONT_FAMILY }}>{t('total_cumtomer')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 26, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{customerCount}</Typography>
                            </Card>
                            <div
                                onClick={() => {
                                    //console.log('[DEBUG] All drivers card clicked');
                                    setMapFilter('all');
                                    setSelectedMarkerId(null);
                                }}
                                style={{ borderRadius: "19px", backgroundColor: colors.VIOLET, minHeight: 100, marginBottom: 20, cursor: 'pointer', opacity: mapFilter === 'all' ? 1 : 0.7, padding: '15px', boxSizing: 'border-box' }}
                            >
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 20, marginTop: 20, fontFamily: FONT_FAMILY }}>{t('total_drivers')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 26, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{driverCount}</Typography>
                            </div>
                            <div
                                onClick={() => {
                                    //console.log('[DEBUG] Active drivers card clicked');
                                    setMapFilter('activeDrivers');
                                    setSelectedMarkerId(null);
                                }}
                                style={{ borderRadius: "19px", backgroundColor: colors.GREEN, minHeight: 100, cursor: 'pointer', opacity: mapFilter === 'activeDrivers' ? 1 : 0.7, padding: '15px', boxSizing: 'border-box' }}
                            >
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 20, marginTop: 20, fontFamily: FONT_FAMILY }}>{t('active_driver')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 26, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{activeCount}</Typography>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
                <Box style={{ padding: 10, marginTop: 20, borderRadius: 20, backgroundColor: colors.WHITE }} boxShadow={6}>
                    <Typography style={{ margin: "5px 20px 0 15px", color: colors.GREEN, fontWeight: "bold", fontFamily: FONT_FAMILY }} variant="h5">{t('booking_chart')}</Typography>
                    <Chart
                        options={barChartOptions}
                        series={barChart}
                        type="area"
                        width="100%"
                        height={360}
                    />
                </Box>
                {auth.profile.usertype === 'admin' ? (
                    <Box
                        sx={{
                            padding: { xs: '14px', sm: '20px' },
                            marginTop: '20px',
                            borderRadius: '20px',
                            backgroundColor: colors.WHITE
                        }}
                        boxShadow={6}
                    >
                        <Typography
                            sx={{
                                margin: '5px 0 15px 0',
                                color: colors.GREEN,
                                fontWeight: 'bold',
                                fontFamily: FONT_FAMILY,
                                fontSize: { xs: 20, sm: 24 }
                            }}
                            variant="h5"
                        >
                            {t('driver_online_stats')}
                        </Typography>
                        {driverStats.length > 0 ? (
                            <>
                                <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
                                    <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse', fontFamily: FONT_FAMILY }}>
                                        <thead>
                                            <tr style={{ borderBottom: `1px solid ${colors.LIGHT_GRAY || '#E5E7EB'}` }}>
                                                <th style={{ textAlign: 'left', padding: '12px 10px' }}>{t('rank')}</th>
                                                <th style={{ textAlign: 'left', padding: '12px 10px' }}>{t('driver_name')}</th>
                                                <th style={{ textAlign: 'left', padding: '12px 10px' }}>{t('status_text')}</th>
                                                <th style={{ textAlign: 'left', padding: '12px 10px' }}>{t('last_online')}</th>
                                                <th style={{ textAlign: 'left', padding: '12px 10px' }}>{t('online_duration')}</th>
                                                <th style={{ textAlign: 'left', padding: '12px 10px' }}>{t('online_sessions')}</th>
                                                <th style={{ textAlign: 'left', padding: '12px 10px' }}>{t('current_session')}</th>
                                                <th style={{ textAlign: 'left', padding: '12px 10px' }}>{t('last_session')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {driverStats.map((driver, index) => (
                                                <tr key={driver.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                                    <td style={{ padding: '12px 10px' }}>{index + 1}</td>
                                                    <td style={{ padding: '12px 10px', fontWeight: 600 }}>{driver.name}</td>
                                                    <td style={{ padding: '12px 10px' }}>{renderStatusBadge(driver)}</td>
                                                    <td style={{ padding: '12px 10px', whiteSpace: 'nowrap' }}>{driver.lastOnlineLabel}</td>
                                                    <td style={{ padding: '12px 10px' }}>{driver.totalOnlineLabel}</td>
                                                    <td style={{ padding: '12px 10px' }}>{driver.sessions}</td>
                                                    <td style={{ padding: '12px 10px' }}>{driver.currentSessionLabel}</td>
                                                    <td style={{ padding: '12px 10px' }}>{driver.lastSessionLabel}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Box>
                                <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: '12px' }}>
                                    {driverStats.map((driver, index) => (
                                        <Box
                                            key={driver.id}
                                            sx={{
                                                border: '1px solid #E2E8F0',
                                                borderRadius: '18px',
                                                padding: '14px',
                                                backgroundColor: '#FFFFFF',
                                                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{
                                                        color: '#0F172A',
                                                        fontSize: 16,
                                                        fontWeight: 800,
                                                        lineHeight: 1.25,
                                                        fontFamily: FONT_FAMILY,
                                                        overflowWrap: 'anywhere'
                                                    }}>
                                                        {index + 1}. {driver.name}
                                                    </Typography>
                                                    <Box sx={{ marginTop: '7px' }}>
                                                        {renderStatusBadge(driver)}
                                                    </Box>
                                                </Box>
                                                <Box sx={{
                                                    flexShrink: 0,
                                                    borderRadius: '999px',
                                                    backgroundColor: '#ECFDF5',
                                                    color: colors.GREEN,
                                                    fontSize: 12,
                                                    fontWeight: 800,
                                                    fontFamily: FONT_FAMILY,
                                                    padding: '6px 10px',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {driver.totalOnlineLabel}
                                                </Box>
                                            </Box>
                                            <Box sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                                gap: '10px',
                                                marginTop: '12px'
                                            }}>
                                                {renderDriverStatTile(t('last_online'), driver.lastOnlineLabel)}
                                                {renderDriverStatTile(t('online_duration'), driver.totalOnlineLabel)}
                                                {renderDriverStatTile(t('online_sessions'), driver.sessions)}
                                                {renderDriverStatTile(t('current_session'), driver.currentSessionLabel)}
                                                {renderDriverStatTile(t('last_session'), driver.lastSessionLabel)}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        ) : (
                            <Typography style={{ color: '#64748B', fontFamily: FONT_FAMILY }}>
                                {t('no_driver_online_stats')}
                            </Typography>
                        )}
                    </Box>
                ) : null}
            </div>
    );
};

export default Dashboard;
